'use client';

import React, { useMemo, useState } from 'react';
import { faHospitalUser } from '@fortawesome/pro-solid-svg-icons';
import useFilterSets from 'src/hooks/filters/useFilterSets';
import { useGetEducationSuggestionsInfiniteQuery } from 'src/api/redux';
import {
  FilterIcon,
  IncludeExcludeSelect,
  ConnectorSelect,
  FilterAutocomplete,
  Divider,
  FilterLabel,
  ClearFilterButton,
} from 'src/components/FilterSets/components';
import { SelectedFilters } from 'src/components/FilterSets/SelectedFilters';
import type { FilterValue } from 'src/context/reducers/filterSetReducer';

const Fellowship = ({ location }: { location: string }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const { filterSet, updateFilter, removeFilterValue } =
    useFilterSets(location);
  const filterValue = filterSet.fellowships;

  const { data, isLoading, isFetching, hasNextPage, fetchNextPage } =
    useGetEducationSuggestionsInfiniteQuery({
      q: searchTerm,
      program_type: 'fellowship',
    });

  const options = useMemo(() => {
    if (!data?.pages) return [];
    return data.pages.flatMap((page) =>
      (page.results ?? []).map((result) => ({
        value: result.id,
        label: result.name,
      }))
    );
  }, [data]);

  const handleRemoveFilter = (filterLabel: string) => {
    removeFilterValue('fellowships', filterLabel);
  };

  const handleFilterChange = (value: FilterValue) => {
    updateFilter('fellowships', value);
  };

  const handleSearch = (term: string) => {
    setSearchTerm(term);
  };

  const hasValues = filterValue?.values?.length;

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between">
        <FilterLabel>Fellowship</FilterLabel>
        <ClearFilterButton
          type="fellowships"
          isDisabled={!hasValues}
          location={location}
        />
      </div>
      <div className="self-stretch px-4 py-2 bg-white rounded-full outline outline-1 -outline-offset-1 outline-neutral-300 inline-flex justify-between items-center">
        <div className="flex-1 flex justify-start items-center gap-2">
          <FilterIcon icon={faHospitalUser} />
          <IncludeExcludeSelect
            title="Fellowship"
            value={filterValue}
            onChange={handleFilterChange}
          />
          <Divider />
          <ConnectorSelect
            title="Fellowship"
            value={filterValue}
            onChange={handleFilterChange}
            isAll={filterValue?.is_all}
          />
          <Divider />
          <FilterAutocomplete
            options={options}
            value={filterValue}
            onChange={handleFilterChange}
            isLoading={isLoading}
            placeholder="Search fellowship programs..."
            onSearch={handleSearch}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetching}
            fetchNextPage={fetchNextPage}
          />
        </div>
      </div>
      <SelectedFilters
        filters={filterValue?.values.map((v) => v.label)}
        onRemove={handleRemoveFilter}
        isAll={filterValue?.is_all}
      />
    </div>
  );
};

export default Fellowship;
